import type { Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import { NODE_TYPES } from '../types';

function isVisibleBranchEntryNode(node: Node<BaseNodeData>): boolean {
	return node.hidden !== true && (node.type === NODE_TYPES.SEGMENT || node.type === NODE_TYPES.BEHAVIOR_CASE);
}

function getSemanticNodeKey(node: Node<BaseNodeData>): string | null {
	if (node.data == null || typeof node.data !== 'object') {
		return null;
	}
	// BaseNodeData extends Record<string, unknown>, so node.data is indexable after the null guard.
	const data = node.data;
	const stepId = typeof data.stepId === 'string' && data.stepId.length > 0 ? data.stepId : null;
	if (stepId) {
		return `step:${stepId}`;
	}

	if (node.type === NODE_TYPES.SEGMENT || node.type === NODE_TYPES.BEHAVIOR_CASE) {
		const caseId = typeof data.caseId === 'string' && data.caseId.length > 0 ? data.caseId : null;
		if (caseId) {
			return `case:${caseId}`;
		}
	}

	return null;
}

export function countVisibleBranchEntryNodes(nodes: Node<BaseNodeData>[]): number {
	let count = 0;
	for (const node of nodes) {
		if (isVisibleBranchEntryNode(node)) {
			count++;
		}
	}
	return count;
}

function getSemanticKeySet(
	nodes: Node<BaseNodeData>[],
	predicate?: (node: Node<BaseNodeData>) => boolean
): Set<string> {
	const keys = new Set<string>();
	for (const node of nodes) {
		if (predicate && !predicate(node)) {
			continue;
		}
		const semanticKey = getSemanticNodeKey(node);
		if (semanticKey) {
			keys.add(semanticKey);
		}
	}
	return keys;
}

function countMatchingSemanticNodes(
	currentNodes: Node<BaseNodeData>[],
	incomingNodes: Node<BaseNodeData>[],
	predicate?: (node: Node<BaseNodeData>) => boolean
): number {
	const currentKeys = getSemanticKeySet(currentNodes, predicate);
	if (currentKeys.size === 0) {
		return 0;
	}

	let matchCount = 0;
	for (const semanticKey of getSemanticKeySet(incomingNodes, predicate)) {
		if (currentKeys.has(semanticKey)) {
			matchCount++;
		}
	}
	return matchCount;
}

/**
 * Ignore incoming branch graphs that are structurally smaller than the current
 * canvas graph, or that still cannot be matched back onto the optimistic branch
 * entries already on the canvas.
 *
 * This protects optimistic branch creation flows from intermediate server
 * snapshots (for example: hidden switch step persisted, but cases/segments not
 * yet refetched). Accepting those partial snapshots makes the canvas oscillate
 * between the optimistic graph and the still-settling server graph.
 */
export function shouldDeferIncomingBranchGraphSync({
	currentNodes,
	incomingNodes,
}: {
	currentNodes: Node<BaseNodeData>[];
	incomingNodes: Node<BaseNodeData>[];
}): boolean {
	const currentBranchCount = countVisibleBranchEntryNodes(currentNodes);
	const incomingBranchCount = countVisibleBranchEntryNodes(incomingNodes);

	if (incomingBranchCount < currentBranchCount) {
		return true;
	}

	if (currentBranchCount === 0 || incomingBranchCount === 0) {
		return false;
	}

	// Only apply the optimistic-branch guard when the two graphs clearly refer to
	// the same flow. Otherwise we may block legitimate template switches.
	const matchingPersistentNodeCount = countMatchingSemanticNodes(currentNodes, incomingNodes);
	if (matchingPersistentNodeCount === 0) {
		return false;
	}

	const matchingBranchEntryCount = countMatchingSemanticNodes(currentNodes, incomingNodes, isVisibleBranchEntryNode);
	return matchingBranchEntryCount < Math.min(currentBranchCount, incomingBranchCount);
}

/**
 * Preserves geometry and local UI state when the incoming server graph uses new
 * React Flow node IDs for nodes that already exist optimistically on the canvas.
 *
 * Examples:
 * - optimistic switch node id -> persisted step-{id}
 * - optimistic segment node id -> persisted segment-{switchId}-{caseId}
 */
export function mergeIncomingNodeLayout({
	currentNodes,
	incomingNodes,
}: {
	currentNodes: Node<BaseNodeData>[];
	incomingNodes: Node<BaseNodeData>[];
}): {
	nodes: Node<BaseNodeData>[];
	matchedCount: number;
} {
	const currentNodeById = new Map(currentNodes.map(node => [node.id, node] as const));
	const currentNodeBySemanticKey = new Map<string, Node<BaseNodeData>>();

	for (const currentNode of currentNodes) {
		const semanticKey = getSemanticNodeKey(currentNode);
		if (semanticKey && !currentNodeBySemanticKey.has(semanticKey)) {
			currentNodeBySemanticKey.set(semanticKey, currentNode);
		}
	}

	let matchedCount = 0;

	const nodes = incomingNodes.map(incomingNode => {
		// Guard: a node without an id cannot be looked up in the current graph.
		if (!incomingNode.id) {
			return incomingNode;
		}

		const semanticKey = getSemanticNodeKey(incomingNode);
		const existingNode =
			currentNodeById.get(incomingNode.id) || (semanticKey ? currentNodeBySemanticKey.get(semanticKey) : undefined);

		if (!existingNode) {
			return incomingNode;
		}

		matchedCount++;

		const existingData = existingNode.data as Record<string, unknown>;
		const preservedData: Record<string, unknown> = {};

		if ('automationStep' in existingData && existingData.automationStep) {
			preservedData.automationStep = existingData.automationStep;
		}
		if ('isExpanded' in existingData) {
			preservedData.isExpanded = existingData.isExpanded;
		}
		if ('isCompact' in existingData) {
			preservedData.isCompact = existingData.isCompact;
		}
		if ('showRequiredFieldError' in existingData) {
			preservedData.showRequiredFieldError = existingData.showRequiredFieldError;
		}

		return {
			...incomingNode,
			position: existingNode.position,
			measured: existingNode.measured,
			data: {
				...incomingNode.data,
				...preservedData,
			},
		};
	});

	return { nodes, matchedCount };
}
